import React, { useCallback, useEffect, useState } from "react";
import { get } from "lodash";
import { v4 as uuid } from "uuid";
import { RaceProgram, RaceStatusEnum, RaceWagerType } from "@tvg/ts-types/Race";
import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import {
  simpleBetTypes,
  exoticsBet,
  getActiveButtons,
  getBetTypeByPosition
} from "@tvg/desktop-bet/src/utils/betUtils";
import BetUtils from "@tvg/utils/betSelection";

// Store
import { ToastMessageLink } from "@tvg/design-system";
import { batch, useDispatch, useSelector } from "react-redux";
import {
  getBetAmount,
  getBetLeg,
  getBetSelections,
  getBetType,
  getHasUserSetAmount,
  getScratchedSelections
} from "@tvg/desktop-bet/src/store/selectors";
import {
  resetBetting,
  setBetType,
  setBetAmount,
  setRaceOfBet,
  setSelectionScratched,
  setSelectionRequest,
  setBetSlipOpen,
  setPickLeg,
  setHasUserSetAmount
} from "@tvg/desktop-bet/src/store/actions";
import { getUserBetAmountPref } from "@tvg/sh-lib-preferences/redux/selectors";
// Types
import { addToast, removeToast } from "../../store/actions";
import getSimpleBets from "./simpleBets";
import getExoticBets from "./exoticBets";
import { filterScratchedSelections, getNumberOfSelections } from "./utils";
// GTM
import { betSlipUndoGTMEvent } from "../../utils/gtm/betSlipUndo";
import { betButtonClickEvent } from "../../utils/gtm/selection";
import getLegBets from "./legBets";

interface UseSetBetSelectionsProps {
  selectedBetTypeCode: WagerTypeCodesEnum;
  race: RaceProgram;
  races: RaceProgram[];
  selectedBetType?: RaceWagerType;
  columnCount: number;
}

const useSetBetSelections = ({
  selectedBetTypeCode,
  race,
  races,
  selectedBetType,
  columnCount
}: UseSetBetSelectionsProps) => {
  const dispatch = useDispatch();
  const [toastId, setToastId] = useState("");
  const storedSelections = useSelector(getBetSelections);
  const storedBetAmount = useSelector(getBetAmount);
  const storedHasUserSetAmount = useSelector(getHasUserSetAmount);
  const storedBetType = useSelector(getBetType);
  const storedBetLeg = useSelector(getBetLeg);
  const scratchedSelections = useSelector(getScratchedSelections);
  const betAmountPreference = useSelector(getUserBetAmountPref);
  const status = get(race, "status.code");

  const onClickBetButton = useCallback(
    (runner, type, position, module) => {
      if (simpleBetTypes.includes(selectedBetTypeCode) || columnCount === 1) {
        const currentSelectionsSimpleBets = getSimpleBets(
          type,
          selectedBetTypeCode,
          storedSelections,
          runner,
          position
        );

        if (!storedHasUserSetAmount) {
          const isSimpleSelection =
            currentSelectionsSimpleBets.filter(
              (selection) => selection.length > 0
            ).length === 1;

          let wager = selectedBetType;

          if (isSimpleSelection) {
            const selectionIndex = currentSelectionsSimpleBets.findIndex(
              (selection) => selection.length > 0
            );

            const selectionWagerType = getBetTypeByPosition(
              selectedBetTypeCode,
              selectionIndex
            );

            wager = selectedBetType?.types?.find(
              (betType) => betType.type.code === selectionWagerType
            );
          }

          const betAmount = BetUtils.getBetAmountWPSBetSelections(
            wager,
            betAmountPreference
          );

          dispatch(setBetAmount(betAmount));
        }

        dispatch(setSelectionRequest(currentSelectionsSimpleBets));
      } else if ((selectedBetType?.legCount || 0) > 1) {
        const runnerFormatted =
          typeof runner === "string" ? runner : `${runner.biNumber}`;
        const currentSelectionsLegBets = getLegBets(
          storedSelections,
          runnerFormatted,
          position,
          race,
          races
        );
        dispatch(setSelectionRequest(currentSelectionsLegBets));
      } else {
        const isTrioWheel =
          selectedBetType?.type.code === WagerTypeCodesEnum.TRIO_WHEEL;

        const currentSelectionsExoticBets = getExoticBets(
          runner,
          position,
          storedSelections,
          !!selectedBetType?.isKey,
          isTrioWheel
        );

        dispatch(setSelectionRequest(currentSelectionsExoticBets));
      }

      const activeButtonsParam =
        !!runner?.biNumber && runner?.biNumber.toString();

      const activeButtons = getActiveButtons(
        activeButtonsParam || runner,
        storedSelections
      );

      if (!activeButtons.every((isActive) => isActive)) {
        const isExotic =
          selectedBetType && exoticsBet.includes(selectedBetType.type.code);
        const optionSelection = type.charAt(0).toUpperCase() + type.slice(1);
        const option = isExotic ? `Exotic ${optionSelection}` : optionSelection;

        betButtonClickEvent({
          module,
          option,
          currentRace: race,
          betTypeCode: selectedBetType?.type.code
        });
      }
    },
    [
      JSON.stringify(storedSelections),
      races,
      selectedBetTypeCode,
      storedHasUserSetAmount
    ]
  );
  useEffect(() => {
    if (getNumberOfSelections(storedSelections) > 0) {
      dispatch(removeToast(toastId));
    }
  }, [JSON.stringify(storedSelections)]);

  useEffect(() => {
    dispatch(
      setBetSlipOpen(
        getNumberOfSelections(storedSelections) > 0 &&
          status !== RaceStatusEnum.RACE_OFFICIAL &&
          status !== RaceStatusEnum.RACE_OFF
      )
    );
  }, [storedSelections, status]);

  useEffect(() => {
    if (selectedBetType) {
      const { filteredSelections, scratchSelections } =
        filterScratchedSelections(
          storedSelections,
          scratchedSelections,
          race,
          races,
          selectedBetType
        );
      if (getNumberOfSelections(storedSelections) > 0) {
        dispatch(setSelectionScratched(scratchSelections));
        dispatch(setSelectionRequest(filteredSelections));
      }
    }
  }, [JSON.stringify(races)]);

  const betSlipUndoGTM = useCallback(() => {
    betSlipUndoGTMEvent();
  }, []);

  const onDelete = useCallback(() => {
    batch(() => {
      dispatch(resetBetting());
    });
    const raceOfBet = race.id;

    setToastId(uuid());

    dispatch(
      addToast({
        id: toastId,
        variant: "informational",
        title: "",
        message: "Your betslip has been cleared",
        hasDismissButton: true,
        qaLabel: "bet-slip-cleared-toast",
        onHide: () => dispatch(removeToast(toastId)),
        onDismissPress: () => dispatch(removeToast(toastId)),
        actionLink: (
          <ToastMessageLink
            variant="success"
            onPress={() => {
              dispatch(setBetType(storedBetType));
              betSlipUndoGTM();
              dispatch(removeToast(toastId));
              dispatch(setPickLeg(storedBetLeg));
              dispatch(setRaceOfBet(raceOfBet || ""));
              dispatch(setBetType(storedBetType));
              setTimeout(() => {
                dispatch(setSelectionRequest(storedSelections));
                dispatch(setBetAmount(storedBetAmount));
                dispatch(setHasUserSetAmount(storedHasUserSetAmount));
                dispatch(setBetSlipOpen(true));
              }, 500);
            }}
            qaLabel="delete-selection-notification"
          >
            Undo
          </ToastMessageLink>
        )
      })
    );
  }, [
    storedSelections,
    storedBetAmount,
    storedBetType,
    storedBetLeg,
    storedHasUserSetAmount
  ]);

  return {
    isBetSlipVisible:
      getNumberOfSelections(storedSelections) > 0 &&
      status !== RaceStatusEnum.RACE_OFFICIAL &&
      status !== RaceStatusEnum.RACE_OFF,
    onClickBetButton,
    onDelete
  };
};

export default useSetBetSelections;
